import { Division, TeamInfo, SizeQuantities, ProductKey } from '../types';

// Type labels for display
export const TYPE_LABELS: Record<string, string> = {
  // Jerseys
  'full-button': 'Full Button',
  'two-button': 'Two-Button Henley',
  'v-neck': 'V-Neck',
  'crew-neck': 'Crew Neck',
  // Pants
  'standard': 'Standard Baseball Pants',
  'knicker': 'Knicker Style',
  'shorts': 'Shorts',
  // Socks
  'crew': 'Crew Socks',
  'stirrup': 'Stirrup Socks',
  'over-the-calf': 'Over-the-Calf',
  // Caps
  'fitted': 'Fitted',
  'adjustable': 'Adjustable',
  'flexfit': 'Flexfit',
  'visor': 'Visor',
};

// Jersey type options
export const JERSEY_TYPES = [
  { value: 'full-button', label: 'Full Button' },
  { value: 'two-button', label: 'Two-Button Henley' },
  { value: 'v-neck', label: 'V-Neck' },
  { value: 'crew-neck', label: 'Crew Neck' },
];

// Pants type options
export const PANTS_TYPES = [
  { value: 'standard', label: 'Standard Baseball Pants' },
  { value: 'knicker', label: 'Knicker Style' },
  { value: 'shorts', label: 'Shorts' },
];

// Socks type options
export const SOCKS_TYPES = [
  { value: 'crew', label: 'Crew Socks' },
  { value: 'stirrup', label: 'Stirrup Socks' },
  { value: 'over-the-calf', label: 'Over-the-Calf' },
];

// Caps type options
export const CAPS_TYPES = [
  { value: 'fitted', label: 'Fitted' },
  { value: 'adjustable', label: 'Adjustable (Velcro/Snap)' },
  { value: 'flexfit', label: 'Flexfit' },
  { value: 'visor', label: 'Visor' },
];

// Size definitions for each product - matches League Uniform Management exactly
export const PRODUCT_SIZES: Record<ProductKey, string[]> = {
  jerseys: [], // Uses separate youth/adult/coach grids
  pants: [],   // Uses separate youth/adult grids
  belts: ['Youth', 'Adult'],
  socks: ['XS', 'S', '6-8', '9-11', '10-13'],
  caps: ['Youth', 'Adult'],
};

// Jersey youth sizes (matches YXXS, YXS, YS, YM, YL, YXL)
export const JERSEY_YOUTH_SIZES = ['YXXS', 'YXS', 'YS', 'YM', 'YL', 'YXL'];

// Jersey/Pants adult sizes (matches AXS, AS, AM, AL, AXL, A2X, A3X)
export const ADULT_SIZES = ['AXS', 'AS', 'AM', 'AL', 'AXL', 'A2X', 'A3X'];

// Pants youth sizes (matches YXS, YS, YM, YL, YXL - no YXXS)
export const PANTS_YOUTH_SIZES = ['YXS', 'YS', 'YM', 'YL', 'YXL'];

// Coach sizes (S, M, L, XL, 2X, 3X)
export const COACH_SIZES = ['S', 'M', 'L', 'XL', '2X', '3X'];

// Sock size labels - must match League Uniform Management exactly
export const SOCK_SIZE_LABELS: Record<string, string> = {
  'XS': 'XS (Youth 8-12)',
  'S': 'Small (Youth 12-5/Ladies 4-7)',
  '6-8': 'Medium (Men 6-8/Ladies 7-10)',
  '9-11': 'Large (Men 9-12/Ladies 10-13)',
  '10-13': 'XL (Mens 12-15/Ladies 13+)',
};

// Get empty sizes object for a product
export const getEmptySizes = (productKey: ProductKey): SizeQuantities => {
  const sizes: SizeQuantities = {};
  PRODUCT_SIZES[productKey].forEach(size => {
    sizes[size] = 0;
  });
  return sizes;
};

// Get all teams as flat list with division info
export const getAllTeams = (divisions: Division[]): TeamInfo[] => {
  const teams: TeamInfo[] = [];
  divisions.forEach((division, divIndex) => {
    division.teams.forEach((team, teamIndex) => {
      teams.push({
        key: `${divIndex}-${teamIndex}`,
        teamName: team.name || `Team ${teamIndex + 1}`,
        teamColor: team.color,
        divisionName: division.name || `Division ${divIndex + 1}`,
        divIndex,
        teamIndex,
      });
    });
  });
  return teams;
};

// Format sizes for display
export const formatSizes = (sizes: SizeQuantities | undefined): string | null => {
  if (!sizes) return null;
  const nonZeroSizes = Object.entries(sizes)
    .filter(([_, qty]) => qty > 0)
    .map(([size, qty]) => `${size}: ${qty}`);
  return nonZeroSizes.length > 0 ? nonZeroSizes.join(', ') : null;
};

// Format date for display
export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Not specified';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Email validation regex
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
